import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { ProviderSettingsSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess, showWarning } from "../../components/ToastNotifications";
import "./AIProviderSettings.css";
import "./PaymentGateways.css";

const API = "/api/admin/payment-gateways";
const environments = ["test", "live"];

const money = (amount, currency = "INR") => new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format((Number(amount) || 0) / 100);
const when = (value) => value ? new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value)) : "Never";
const label = (value) => String(value || "").replaceAll("_", " ");
const hintFor = (field, environment) => typeof field.hint === "string" ? field.hint : field.hint?.[environment] || "";

const auditLabels = {
  PAYMENT_GATEWAY_CREDENTIALS_UPDATED: "Credentials updated",
  PAYMENT_GATEWAY_ENVIRONMENT_CHANGED: "Environment changed",
  PAYMENT_GATEWAY_ACTIVATED: "Activated",
  PAYMENT_GATEWAY_DEACTIVATED: "Deactivated",
  PAYMENT_GATEWAY_CONNECTION_TESTED: "Connection tested",
  PAYMENT_REFUND_REQUESTED: "Refund requested"
};

// Blank form for one gateway: public values prefilled, secrets always empty (saved values are never sent back).
const emptyDraft = (gateway) => ({
  environment: gateway.environment,
  editing: gateway.environment,
  credentials: Object.fromEntries(environments.map((env) => [env, Object.fromEntries(gateway.fields.map((field) => [
    field.key,
    field.secret ? "" : gateway.credentials[env].values[field.key] || (field.options ? field.defaultValue || field.options[0] : "")
  ]))]))
});

const ConfirmDialog = ({ dialog, busy, onCancel, onConfirm }) => {
  const confirmRef = useRef(null);
  useEffect(() => { confirmRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);
  return <div className="admin-pay-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
    <section className="admin-pay-modal admin-pay-confirm" role="alertdialog" aria-modal="true" aria-labelledby="admin-pay-confirm-title" aria-describedby="admin-pay-confirm-body">
      <div className={`admin-pay-confirm-icon${dialog.tone === "danger" ? " is-danger" : ""}`} aria-hidden="true"><i className={`ti ${dialog.icon || "ti-alert-triangle"}`} /></div>
      <h2 id="admin-pay-confirm-title">{dialog.title}</h2>
      <div id="admin-pay-confirm-body" className="admin-pay-confirm-body">{dialog.body}</div>
      <div className="admin-pay-modal-actions">
        <button type="button" className="admin-ai-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" ref={confirmRef} className={`admin-ai-primary${dialog.tone === "danger" ? " admin-pay-danger" : ""}`} onClick={onConfirm} disabled={busy}>
          {busy && <i className="ti ti-loader-2 admin-pay-spin" aria-hidden="true" />}{busy ? "Working…" : dialog.confirmLabel}
        </button>
      </div>
    </section>
  </div>;
};

const CopyField = ({ id, labelText, value }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccess(`${labelText} copied.`);
    } catch (error) {
      showWarning("Copy isn't available here. Select the URL and copy it manually.");
    }
  };
  return <div className="admin-pay-copy">
    <label htmlFor={id}>{labelText}</label>
    <div><input id={id} value={value} readOnly onFocus={(event) => event.target.select()} /><button type="button" className="admin-ai-secondary" onClick={copy} aria-label={`Copy ${labelText.toLowerCase()}`}><i className="ti ti-copy" aria-hidden="true" /></button></div>
  </div>;
};

const GatewayEditor = ({ gateway, onClose, onSaved, onTest, testing }) => {
  const initial = useMemo(() => emptyDraft(gateway), [gateway]);
  const [draft, setDraft] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const firstFieldRef = useRef(null);
  const env = draft.editing;
  const view = gateway.credentials[env];

  useEffect(() => { firstFieldRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !saving && !confirmLive) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmLive, onClose, saving]);

  const setValue = (key, value) => {
    setDraft((current) => ({ ...current, credentials: { ...current.credentials, [env]: { ...current.credentials[env], [key]: value } } }));
    setErrors((current) => ({ ...current, [`${env}.${key}`]: undefined }));
  };

  // Required fields must be filled, or already saved for secrets, before an environment can be used.
  const validate = () => {
    const next = {};
    for (const environment of environments) {
      const values = draft.credentials[environment];
      const touched = gateway.fields.some((field) => (values[field.key] || "") !== (initial.credentials[environment][field.key] || ""));
      const mustBeComplete = environment === draft.environment && (gateway.isActive || draft.environment !== gateway.environment);
      if (!touched && !mustBeComplete) continue;
      for (const field of gateway.fields.filter((item) => item.required)) {
        const saved = field.secret ? gateway.credentials[environment].secrets[field.key] : false;
        if (!String(values[field.key] || "").trim() && !saved) next[`${environment}.${field.key}`] = `${field.label} is required for ${gateway.environmentLabels[environment]}.`;
      }
    }
    setErrors(next);
    const firstError = Object.keys(next)[0];
    if (firstError) setDraft((current) => ({ ...current, editing: firstError.split(".")[0] }));
    return !firstError;
  };

  const save = async (liveConfirmed = false) => {
    if (!validate()) return showError("Complete the highlighted fields before saving.");
    if (draft.environment === "live" && gateway.environment !== "live" && !liveConfirmed) return setConfirmLive(true);
    setSaving(true);
    try {
      const credentials = Object.fromEntries(environments.map((environment) => [environment, Object.fromEntries(
        gateway.fields
          .filter((field) => field.secret ? draft.credentials[environment][field.key] : true)
          .map((field) => [field.key, String(draft.credentials[environment][field.key] || "").trim()])
      )]));
      const result = await apiRequest(`${API}/${gateway.id}`, {
        method: "PUT",
        body: JSON.stringify({ environment: draft.environment, confirmLive: liveConfirmed || undefined, credentials })
      });
      showSuccess(result.message);
      onSaved(result.gateway);
    } catch (error) {
      showError(error.message || `Unable to save ${gateway.name} settings.`);
    } finally {
      setSaving(false);
      setConfirmLive(false);
    }
  };

  const sections = [
    ["credentials", "Credentials", "ti-key"],
    ["webhooks", "Webhooks", "ti-webhook"]
  ];

  return <div className="admin-pay-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className="admin-pay-modal" role="dialog" aria-modal="true" aria-labelledby="admin-pay-editor-title">
      <div className="admin-pay-modal-heading">
        <div className="admin-pay-modal-title">
          <span className={`admin-ai-provider-icon admin-pay-icon is-${gateway.id}`} aria-hidden="true"><i className={`ti ${gateway.icon}`} /></span>
          <div><p className="workspace-eyebrow">Configure payment gateway</p><h2 id="admin-pay-editor-title">{gateway.name}</h2></div>
        </div>
        <button type="button" className="admin-pay-close" aria-label="Close" onClick={onClose} disabled={saving}><i className="ti ti-x" aria-hidden="true" /></button>
      </div>

      <form onSubmit={(event) => { event.preventDefault(); save(); }} noValidate>
        <fieldset className="admin-pay-section">
          <legend><i className="ti ti-world" aria-hidden="true" /> Environment</legend>
          <p className="admin-pay-help">Payments use the selected mode. Test and live credentials are stored separately and never mixed.</p>
          <div className="admin-pay-segment" role="radiogroup" aria-label="Payment mode">
            {environments.map((environment) => <label key={environment} className={draft.environment === environment ? "is-selected" : ""}>
              <input type="radio" name="environment" value={environment} checked={draft.environment === environment} onChange={() => setDraft((current) => ({ ...current, environment, editing: environment }))} />
              <span>{gateway.environmentLabels[environment]}</span>
              {environment === "live" && <small>Real charges</small>}
            </label>)}
          </div>
          {draft.environment === "live" && <div className="admin-ai-warning admin-pay-inline-warning" role="note"><i className="ti ti-alert-triangle" aria-hidden="true" /><p>Live mode charges customers real money. Complete {gateway.name}'s account activation and test the connection before activating.</p></div>}
        </fieldset>

        <div className="admin-pay-env-tabs" role="tablist" aria-label="Credentials to edit">
          {environments.map((environment) => <button key={environment} type="button" role="tab" aria-selected={env === environment} className={env === environment ? "is-active" : ""} onClick={() => setDraft((current) => ({ ...current, editing: environment }))}>
            {gateway.environmentLabels[environment]}
            <span className={`admin-pay-dot ${gateway.credentials[environment].configured ? "is-ok" : ""}`} aria-label={gateway.credentials[environment].configured ? "configured" : "not configured"} />
          </button>)}
        </div>

        {sections.map(([section, title, icon]) => {
          const fields = gateway.fields.filter((field) => field.section === section);
          return <fieldset className="admin-pay-section" key={section}>
            <legend><i className={`ti ${icon}`} aria-hidden="true" /> {title} <span className="admin-pay-legend-env">· {gateway.environmentLabels[env]}</span></legend>
            {section === "webhooks" && <>
              <CopyField id={`${gateway.id}-webhook-url`} labelText="Webhook URL" value={gateway.webhookUrl} />
              {gateway.callbackUrl && <CopyField id={`${gateway.id}-callback-url`} labelText="Return / callback URL" value={gateway.callbackUrl} />}
              <p className="admin-pay-help">Events: {gateway.webhookEvents.join(", ")}</p>
            </>}
            <div className="admin-pay-fields">
              {fields.map((field, index) => {
                const id = `${gateway.id}-${env}-${field.key}`;
                const error = errors[`${env}.${field.key}`];
                const hint = hintFor(field, env);
                const saved = field.secret && view.secrets[field.key];
                return <div className="admin-ai-credential-field admin-pay-field" key={field.key}>
                  <label htmlFor={id}>{field.label}{field.required ? <span aria-hidden="true"> *</span> : <span className="admin-pay-optional"> (optional)</span>}{saved && <span className="admin-pay-saved"><i className="ti ti-lock" aria-hidden="true" /> Saved</span>}</label>
                  {field.options ? <select id={id} value={draft.credentials[env][field.key]} onChange={(event) => setValue(field.key, event.target.value)}>
                    {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select> : <input
                    id={id}
                    ref={section === "credentials" && index === 0 ? firstFieldRef : undefined}
                    type={field.secret ? "password" : "text"}
                    autoComplete={field.secret ? "new-password" : "off"}
                    spellCheck="false"
                    maxLength={512}
                    value={draft.credentials[env][field.key]}
                    onChange={(event) => setValue(field.key, event.target.value)}
                    placeholder={saved ? "•••••••• saved securely · enter a new value to replace" : `Enter ${field.label.toLowerCase()}`}
                    aria-invalid={Boolean(error)}
                    aria-describedby={[hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined}
                  />}
                  {hint && <small id={`${id}-hint`} className="admin-pay-hint">{hint}</small>}
                  {error && <small id={`${id}-error`} className="admin-pay-error" role="alert">{error}</small>}
                </div>;
              })}
            </div>
          </fieldset>;
        })}

        <fieldset className="admin-pay-section">
          <legend><i className="ti ti-activity" aria-hidden="true" /> Status</legend>
          <div className="admin-pay-status-grid">
            <div><span>Mode in use</span><strong>{gateway.environmentLabels[gateway.environment]}</strong></div>
            <div><span>Last connection test</span><strong className={gateway.lastTest ? `is-${gateway.lastTest.status}` : ""}>{gateway.lastTest ? `${gateway.lastTest.status === "passed" ? "Passed" : "Failed"} · ${gateway.environmentLabels[gateway.lastTest.environment]}` : "Not tested"}</strong></div>
            <div><span>Last updated</span><strong>{when(gateway.updatedAt)}</strong></div>
          </div>
          {gateway.lastTest?.message && <p className="admin-pay-help">{gateway.lastTest.message}</p>}
          <button type="button" className="admin-ai-secondary" onClick={() => onTest(gateway)} disabled={!gateway.configured || testing || saving}>
            <i className={`ti ${testing ? "ti-loader-2 admin-pay-spin" : "ti-plug-connected"}`} aria-hidden="true" />{testing ? "Testing…" : `Test saved ${gateway.environment} credentials`}
          </button>
          {!gateway.configured && <p className="admin-pay-help">Save complete credentials for the selected mode to enable the connection test.</p>}
        </fieldset>

        <details className="admin-pay-steps">
          <summary><i className="ti ti-list-check" aria-hidden="true" /> {gateway.name} merchant dashboard steps</summary>
          <ol>{gateway.dashboardSteps.map((step) => <li key={step}>{step}</li>)}</ol>
        </details>

        <div className="admin-pay-modal-actions">
          <button type="button" className="admin-ai-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-ai-primary" disabled={saving}><i className={`ti ${saving ? "ti-loader-2 admin-pay-spin" : "ti-device-floppy"}`} aria-hidden="true" />{saving ? "Saving…" : "Save securely"}</button>
        </div>
      </form>
    </section>
    {confirmLive && <ConfirmDialog
      busy={saving}
      dialog={{
        title: `Switch ${gateway.name} to live payments?`,
        body: <p>{gateway.isActive ? "This gateway is active. As soon as you save, customers will be charged real money." : "Customers will be charged real money once this gateway is activated."} Live webhooks must be configured in {gateway.name} with the live signing secret.</p>,
        confirmLabel: "Use live mode",
        tone: "danger"
      }}
      onCancel={() => setConfirmLive(false)}
      onConfirm={() => save(true)}
    />}
  </div>;
};

const PaymentGateways = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState("");
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      setData(await apiRequest(API));
    } catch (error) {
      setPageError(error.message || "Unable to load payment gateway settings.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      setActivity(await apiRequest(`${API}/activity`));
    } catch (error) {
      showError(error.message || "Unable to load payment activity.");
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const gateways = useMemo(() => data?.gateways || [], [data]);
  const active = gateways.find((gateway) => gateway.isActive) || null;
  const editingGateway = gateways.find((gateway) => gateway.id === editing) || null;
  const nameOf = (id) => gateways.find((gateway) => gateway.id === id)?.name || id;

  const replaceGateway = (gateway) => setData((current) => current && ({
    ...current,
    gateways: current.gateways.map((item) => item.id === gateway.id ? gateway : gateway.isActive ? { ...item, isActive: false } : item)
  }));

  const testConnection = async (gateway) => {
    setTesting(gateway.id);
    try {
      const result = await apiRequest(`${API}/${gateway.id}/test`, { method: "POST", body: JSON.stringify({}) });
      showSuccess(result.message);
      replaceGateway(result.gateway);
    } catch (error) {
      showError(error.message || `${gateway.name} connection test failed.`);
      if (error.data?.gateway) replaceGateway(error.data.gateway);
    } finally {
      setTesting("");
    }
  };

  const requestActivation = (gateway) => setDialog({
    kind: "activate",
    gateway,
    title: active ? `Switch the active gateway to ${gateway.name}?` : `Activate ${gateway.name}?`,
    icon: "ti-switch-horizontal",
    tone: gateway.environment === "live" ? "danger" : undefined,
    confirmLabel: active ? `Switch to ${gateway.name}` : "Activate gateway",
    body: <>
      {active && <p><strong>{active.name}</strong> will be deactivated. Only one gateway can be active at a time.</p>}
      <p>All new checkouts, upgrades and renewals will use {gateway.name} in <strong>{gateway.environmentLabels[gateway.environment]}</strong>.{gateway.environment === "live" ? " Customers will be charged real money." : " Test mode does not move real money."}</p>
      {active && <p>Pending and completed payments keep their original gateway, so they can still be verified, reconciled and refunded.</p>}
      <p className="admin-pay-help">A live connection test runs first; activation is cancelled if it fails.</p>
    </>
  });

  const requestDeactivation = (gateway) => setDialog({
    kind: "deactivate",
    gateway,
    title: `Deactivate ${gateway.name}?`,
    icon: "ti-player-pause",
    tone: "danger",
    confirmLabel: "Deactivate and disable checkout",
    body: <p>No other gateway is active, so customers won't be able to buy, upgrade or renew plans until you activate one. Existing subscriptions and pending payments are not affected.</p>
  });

  const requestRefund = (row) => setDialog({
    kind: "refund",
    row,
    title: `Refund ${money(row.amount - (row.refundedAmount || 0), row.currency)}?`,
    icon: "ti-receipt-refund",
    tone: "danger",
    confirmLabel: "Issue refund",
    body: <p>The refund is sent through {nameOf(row.provider)} ({row.environment}), the gateway that took this payment, and cannot be undone. A fully refunded subscription is marked past due.</p>
  });

  const confirm = async () => {
    setBusy(true);
    try {
      if (dialog.kind === "activate") {
        const result = await apiRequest(`${API}/${dialog.gateway.id}/activate`, { method: "POST", body: JSON.stringify({ confirm: true }) });
        showSuccess(result.message);
      } else if (dialog.kind === "deactivate") {
        const result = await apiRequest(`${API}/${dialog.gateway.id}/deactivate`, { method: "POST", body: JSON.stringify({ confirm: true }) });
        showWarning(result.message);
      } else if (dialog.kind === "refund") {
        const result = await apiRequest(`${API}/payments/${dialog.row.paymentId}/refund`, { method: "POST", body: JSON.stringify({ confirm: true }) });
        showSuccess(result.message);
        loadActivity();
      }
      setDialog(null);
      await load();
    } catch (error) {
      showError(error.message || "The action could not be completed.");
      if (dialog.kind === "activate") load();
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async (row) => {
    setRowBusy(`reconcile-${row.id}`);
    try {
      const result = await apiRequest(`${API}/transactions/${row.id}/reconcile`, { method: "POST", body: JSON.stringify({}) });
      showSuccess(result.message);
      await loadActivity();
    } catch (error) {
      showError(error.message || "Unable to re-check this transaction.");
    } finally {
      setRowBusy("");
    }
  };

  if (loading && !data) return <main className="workspace-dashboard admin-ai-settings admin-pay"><ProviderSettingsSkeleton label="Loading payment gateways" banner /></main>;

  return <main className="workspace-dashboard admin-ai-settings admin-pay">
    <header className="workspace-dashboard-header">
      <div>
        <span className="workspace-eyebrow">PLATFORM CONFIGURATION</span>
        <h1>Payment gateways</h1>
        <p>Configure your payment providers and choose the one gateway that processes every checkout, upgrade and renewal.</p>
      </div>
      <button className="admin-ai-secondary" type="button" onClick={load} disabled={loading}><i className={`ti ${loading ? "ti-loader-2 admin-pay-spin" : "ti-refresh"}`} aria-hidden="true" /> Refresh</button>
    </header>

    {pageError && <div className="admin-ai-error" role="alert">
      <i className="ti ti-alert-circle" aria-hidden="true" />
      <div><strong>Settings couldn’t be loaded</strong><p>{pageError}</p></div>
      <button className="admin-ai-secondary" type="button" onClick={load}>Try again</button>
    </div>}

    {data && (active ? <section className={`admin-pay-banner${active.environment === "test" ? " is-test" : " is-live"}`} aria-label="Active payment gateway">
      <span className={`admin-ai-provider-icon admin-pay-icon is-${active.id}`} aria-hidden="true"><i className={`ti ${active.icon}`} /></span>
      <div className="admin-pay-banner-copy">
        <span>Active gateway</span>
        <strong>{active.name}</strong>
        <small>{active.environment === "live" ? "Live payments — customers are charged real money." : "Test mode — no real money moves. Switch to live before launch."}</small>
      </div>
      <dl className="admin-pay-banner-facts">
        <div><dt>Environment</dt><dd><span className={`admin-pay-env is-${active.environment}`}>{active.environmentLabels[active.environment]}</span></dd></div>
        <div><dt>Last test</dt><dd>{active.lastTest ? `${active.lastTest.status === "passed" ? "Passed" : "Failed"} · ${when(active.lastTest.testedAt)}` : "Not tested"}</dd></div>
        <div><dt>Activated</dt><dd>{when(active.activatedAt)}</dd></div>
      </dl>
    </section> : <section className="admin-pay-banner is-off" role="alert">
      <i className="ti ti-credit-card-off" aria-hidden="true" />
      <div className="admin-pay-banner-copy">
        <strong>Checkout is disabled</strong>
        <small>No payment gateway is active, so customers can't buy, upgrade or renew paid plans. Configure a gateway below and activate it.</small>
      </div>
    </section>)}

    <section aria-label="Payment gateways" className="admin-pay-grid">
      {gateways.map((gateway) => {
        const testEnvReady = gateway.credentials.test.configured;
        const liveEnvReady = gateway.credentials.live.configured;
        return <article className={`admin-ai-provider admin-pay-card${gateway.isActive ? " is-active" : ""}`} key={gateway.id} aria-labelledby={`gateway-${gateway.id}-name`}>
          <header className="admin-ai-provider-header">
            <div className={`admin-ai-provider-icon admin-pay-icon is-${gateway.id}`} aria-hidden="true"><i className={`ti ${gateway.icon}`} /></div>
            <div className="admin-ai-provider-title">
              <h3 id={`gateway-${gateway.id}-name`}>{gateway.name}</h3>
              <p>{gateway.description}</p>
            </div>
            <span className={`admin-ai-status ${gateway.isActive ? "is-enabled" : "is-disabled"}`}>
              <i className={`ti ${gateway.isActive ? "ti-circle-check-filled" : "ti-circle-minus"}`} aria-hidden="true" />{gateway.isActive ? "Active" : "Inactive"}
            </span>
          </header>
          <ul className="admin-pay-facts" aria-label={`${gateway.name} status`}>
            <li><span>Configuration</span><strong className={gateway.configured ? "is-ok" : "is-muted"}>{gateway.configured ? "Configured" : testEnvReady || liveEnvReady ? "Incomplete for selected mode" : "Not configured"}</strong></li>
            <li><span>Mode</span><strong><span className={`admin-pay-env is-${gateway.environment}`}>{gateway.environmentLabels[gateway.environment]}</span></strong></li>
            <li><span>Credentials saved</span><strong>{[testEnvReady && "Test", liveEnvReady && "Live"].filter(Boolean).join(" · ") || "None"}</strong></li>
            <li><span>Last test</span><strong className={gateway.lastTest ? `is-${gateway.lastTest.status}` : "is-muted"}>{gateway.lastTest ? `${gateway.lastTest.status === "passed" ? "Passed" : "Failed"} · ${when(gateway.lastTest.testedAt)}` : "Not tested"}</strong></li>
            <li><span>Billing</span><strong>{gateway.capabilities.recurring ? "Automatic renewals" : "Customer pays each renewal"}</strong></li>
            <li><span>Updated</span><strong>{when(gateway.updatedAt)}</strong></li>
          </ul>
          <div className="admin-ai-provider-actions admin-pay-actions">
            <button type="button" className="admin-ai-secondary" onClick={() => setEditing(gateway.id)}><i className="ti ti-settings" aria-hidden="true" />{gateway.configured ? "Edit" : "Configure"}</button>
            {gateway.configured && <button type="button" className="admin-ai-secondary" onClick={() => testConnection(gateway)} disabled={testing === gateway.id}><i className={`ti ${testing === gateway.id ? "ti-loader-2 admin-pay-spin" : "ti-plug-connected"}`} aria-hidden="true" />{testing === gateway.id ? "Testing…" : "Test"}</button>}
            {gateway.isActive
              ? <button type="button" className="admin-ai-secondary admin-pay-danger-text" onClick={() => requestDeactivation(gateway)}><i className="ti ti-player-pause" aria-hidden="true" />Deactivate</button>
              : <button type="button" className="admin-ai-primary" onClick={() => requestActivation(gateway)} disabled={!gateway.configured} title={gateway.configured ? undefined : "Save complete credentials for the selected mode first"}><i className="ti ti-bolt" aria-hidden="true" />Activate</button>}
          </div>
        </article>;
      })}
    </section>

    <section className="admin-pay-panel" aria-labelledby="admin-pay-activity-title">
      <div className="admin-pay-panel-heading">
        <div><h2 id="admin-pay-activity-title">Payment activity</h2><p>Recent checkouts and verified webhooks across every gateway, including inactive ones.</p></div>
        <button type="button" className="admin-ai-secondary" onClick={loadActivity} disabled={activityLoading}><i className={`ti ${activityLoading ? "ti-loader-2 admin-pay-spin" : activity ? "ti-refresh" : "ti-list-search"}`} aria-hidden="true" />{activity ? "Refresh" : "Load activity"}</button>
      </div>
      {activity && <>
        {activity.transactions.length ? <div className="admin-pay-table-scroll"><table className="admin-pay-table">
          <thead><tr><th scope="col">Reference</th><th scope="col">Gateway</th><th scope="col">Status</th><th scope="col">Amount</th><th scope="col">Created</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
          <tbody>{activity.transactions.map((row) => <tr key={row.id}>
            <td><code>{row.reference}</code><small>{row.planName || "Plan"} · {row.purpose}</small>{row.failureReason && <small className="admin-pay-error">{row.failureReason}</small>}</td>
            <td>{nameOf(row.provider)}<small><span className={`admin-pay-env is-${row.environment}`}>{row.environment}</span></small></td>
            <td><span className={`admin-pay-status is-${row.status}`}>{label(row.status)}</span></td>
            <td>{money(row.amount, row.currency)}{row.refundedAmount > 0 && <small>Refunded {money(row.refundedAmount, row.currency)}</small>}</td>
            <td>{when(row.createdAt)}</td>
            <td className="admin-pay-row-actions">
              {["pending", "created", "expired", "failed"].includes(row.status) && <button type="button" className="admin-ai-secondary" onClick={() => reconcile(row)} disabled={rowBusy === `reconcile-${row.id}`}>{rowBusy === `reconcile-${row.id}` ? "Checking…" : "Re-check"}</button>}
              {row.paymentId && ["succeeded", "partially_refunded"].includes(row.status) && <button type="button" className="admin-ai-secondary admin-pay-danger-text" onClick={() => requestRefund(row)}>Refund</button>}
            </td>
          </tr>)}</tbody>
        </table></div> : <div className="admin-pay-empty"><i className="ti ti-receipt-off" aria-hidden="true" /><p>No checkouts yet.</p></div>}
        <h3 className="admin-pay-subheading">Webhook log</h3>
        {activity.webhooks.length ? <ul className="admin-pay-log">{activity.webhooks.map((row) => <li key={row.id}>
          <span className={`admin-pay-status is-${row.status}`}>{row.status}</span>
          <div><strong>{nameOf(row.provider)} · {row.eventType}</strong><small>{row.environment} · {when(row.createdAt)}{row.attempts > 1 ? ` · ${row.attempts} attempts` : ""}{row.error ? ` · ${row.error}` : ""}</small></div>
        </li>)}</ul> : <div className="admin-pay-empty"><i className="ti ti-webhook-off" aria-hidden="true" /><p>No verified webhooks received yet.</p></div>}
      </>}
    </section>

    {data?.history?.length > 0 && <section className="admin-pay-panel" aria-labelledby="admin-pay-history-title">
      <div className="admin-pay-panel-heading"><div><h2 id="admin-pay-history-title">Configuration history</h2><p>Audit trail of gateway changes. Credential values are never recorded.</p></div></div>
      <ul className="admin-pay-log">{data.history.map((entry) => <li key={entry.id}>
        <span className="admin-pay-status is-processed">{auditLabels[entry.action] || label(entry.action)}</span>
        <div><strong>{nameOf(entry.changes?.provider)}{entry.changes?.environment ? ` · ${entry.changes.environment}` : ""}{entry.changes?.from ? ` · ${entry.changes.from} → ${entry.changes.to}` : ""}{entry.changes?.outcome ? ` · ${entry.changes.outcome}` : ""}</strong><small>{when(entry.createdAt)} · Admin #{entry.adminUserId}{entry.changes?.fields ? ` · ${Object.entries(entry.changes.fields).map(([env, fields]) => `${env}: ${fields.join(", ")}`).join("; ")}` : ""}</small></div>
      </li>)}</ul>
    </section>}

    {editingGateway && <GatewayEditor
      gateway={editingGateway}
      testing={testing === editingGateway.id}
      onTest={testConnection}
      onClose={() => setEditing(null)}
      onSaved={(gateway) => { replaceGateway(gateway); setEditing(null); }}
    />}
    {dialog && <ConfirmDialog dialog={dialog} busy={busy} onCancel={() => setDialog(null)} onConfirm={confirm} />}
  </main>;
};

export default PaymentGateways;
